import React, { useMemo, useState } from 'react';
import { calculatePrices, calculatePricesForAllPlatformsAndDestinations } from '../../common/pricesCalculation';
import Big from 'big.js';
import { AdForm_GetAdQuery } from '../../codegen/graphql';
import { FaEbay, FaStore, FaStoreAlt } from 'react-icons/fa';
import AdVariation_SearchSelect from './common/SearchSelect/AdVariation_SearchSelect';
import SupplierVariation_SearchSelect from './common/SearchSelect/SupplierVariation_SearchSelect';

const MARGIN_PERCENT = 35;
const DEFAULT_PACKAGE_WEIGHT = 250;
const DEFAULT_PACKAGE_SIZE = { size1: 27, size2: 27, size3: 10 };

function formatPrice(value: Big): string {
  if (value.lt(0)) return '–';
  return value.toFixed(2) + ' €';
}

interface AdPricesProps {
  adData: NonNullable<AdForm_GetAdQuery['ad']>;
}

export function AdPrices({ adData }: AdPricesProps) {
  const [selectedAdVariation, setSelectedAdVariation] = useState<{ value: string, label: string } | null>(null);
  const [selectedSupplierVariation, setSelectedSupplierVariation] = useState<{ value: string, label: string } | null>(null);

  const pricesResult = useMemo(() => {
    if (!selectedAdVariation || !selectedSupplierVariation) {
      return null;
    }

    const adVariation = adData.variations?.find(v => v.id === selectedAdVariation.value)!;
    const supplierProductVariation = adVariation?.supplierProductVariations?.find(spv => spv.sku === selectedSupplierVariation.value)!;

    const supplierName = supplierProductVariation.supplierName;
    const calculatedCost = supplierProductVariation.calculatedCost.find((cc) => cc.destinationCountry === 'FR')!;
    const productCostExcludingVAT = new Big(calculatedCost.totalCostExcludingVAT);

    const packaging = (supplierProductVariation.packaging as { weight?: number; size1?: number; size2?: number; size3?: number }) || {};
    const packageWeight = (packaging.weight ?? 0) || DEFAULT_PACKAGE_WEIGHT;
    const packageSize1 = packaging.size1 ?? DEFAULT_PACKAGE_SIZE.size1;
    const packageSize2 = packaging.size2 ?? DEFAULT_PACKAGE_SIZE.size2;
    const packageSize3 = packaging.size3 ?? DEFAULT_PACKAGE_SIZE.size3;

    return calculatePricesForAllPlatformsAndDestinations({
      marginPercentOnBaseCost: new Big(MARGIN_PERCENT),
      productCostExcludingVAT: productCostExcludingVAT,
      packingCostExcludingVAT: new Big(0),
      returnRate: new Big(0),
      supplierName: supplierName,
      origin: 'FR',
      packageWeight: packageWeight,
      packageSize1: packageSize1,
      packageSize2: packageSize2,
      packageSize3: packageSize3,
    });
  }, [adData, selectedAdVariation, selectedSupplierVariation]);

  return (<>
    <AdVariation_SearchSelect
      adId={adData.id}
      selectedAdVariation={selectedAdVariation}
      onAdVariationChange={setSelectedAdVariation}
    />
    {selectedAdVariation && (
      <SupplierVariation_SearchSelect
        linkedToAdVariationId={selectedAdVariation.value}
        selectedSupplierVariation={selectedSupplierVariation}
        onSupplierVariationChange={setSelectedSupplierVariation}
      />
    )}
    {!pricesResult && (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <p className="text-gray-500">
          No pricing data available. Add at least one variation with a linked supplier product.
        </p>
      </div>
    )}
    {pricesResult && (
      <div className="space-y-6">
        {Object.entries(pricesResult).map(([platformKey, platformPrices]) => {
          return (
            <div className="bg-white shadow-sm rounded-lg">
              <div className="bg-gray-50 px-4 py-3 flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">{platformKey}</h3>
              </div>
              {Object.entries(platformPrices).map(([destination, destinationPrices]) => (<>
                <div className="bg-gray-50 px-4 py-3 flex items-center gap-2">
                  <span className="text-gray-600">{destination}</span>
                </div>
                {destinationPrices.shippingServiceSelected && (
                  <div className="hover:bg-gray-50">
                    <span className="text-gray-600">Shipping Service</span>
                    <span className="text-gray-900">
                      {destinationPrices.shippingServiceSelected.service.description}
                    </span>
                  </div>
                )}
                <div className="p-4 grid grid-cols-7 gap-2">
                  <div className="col-span-1 flex flex-col gap-2">
                    <span className="text-gray-800 text-lg">Product Cost</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.productCostIncludingVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Product Cost VAT included</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.productCostVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Product Cost (excl. VAT)</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.productCostExcludingVAT)}
                    </span>
                  </div>
                  <div className="col-span-1 flex flex-col gap-2">
                    <span className="text-gray-800 text-lg">Packing Cost</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.packingCostIncludingVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Packing Cost VAT included</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.packingCostVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Packing Cost (excl. VAT)</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.packingCostExcludingVAT)}
                    </span>
                  </div>
                  <div className="col-span-1 flex flex-col gap-2">
                    <span className="text-gray-800 text-lg">Gross Margin</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.grossMarginIncludingVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Gross Margin VAT included</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.grossMarginVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Gross Margin (excl. VAT)</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.grossMarginExcludingVAT)}
                    </span>
                  </div>
                  <div className="col-span-1 flex flex-col gap-2">
                    <span className="text-gray-800 text-lg">Channel Fee</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.channelFeeIncludingVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Channel Fee VAT included</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.channelFeeVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Channel Fee (excl. VAT)</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.channelFeeExcludingVAT)}
                    </span>
                  </div>
                  <div className="col-span-1 flex flex-col gap-2">
                    <span className="text-gray-800 text-lg">Shipping Cost</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.shippingCostIncludingVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Shipping Cost VAT included</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.shippingCostVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Shipping Cost (excl. VAT)</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.shippingCostExcludingVAT)}
                    </span>
                  </div>
                  <div className="col-span-1 flex flex-col gap-2">
                    <span className="text-gray-800 text-lg">Return Shipping Cost</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.returnShippingCostIncludingVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Return Shipping Cost VAT included</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.returnShippingCostVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Return Shipping Cost (excl. VAT)</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.returnShippingCostExcludingVAT)}
                    </span>
                  </div>
                  <div className="col-span-1 flex flex-col gap-2">
                    <span className="text-gray-800 text-lg">Retail Price</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.retailPriceIncludingVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Retail Price VAT included</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.retailPriceVAT)}
                    </span>
                    <span className="text-gray-800 text-lg">Retail Price (excl. VAT)</span><br/>
                    <span className="text-gray-900 text-2xl">
                      {formatPrice(destinationPrices.retailPriceExcludingVAT)}
                    </span>
                  </div>
                </div>
              </>
              ))}
            </div>
          );
        })}
      </div>
    )}
  </>);
}
